import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Header } from "@/components/layout/Header";
import { useCategories } from "@/hooks/useCategories";
import { useProducts } from "@/hooks/useProducts";
import { useVendors } from "@/hooks/useVendors";

export default function Catalog() {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedVendor, setSelectedVendor] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const { data: categories, isLoading: categoriesLoading } = useCategories();
  const { data: vendors, isLoading: vendorsLoading } = useVendors();
  const { data: productsData, isLoading: productsLoading } = useProducts({
    categoryId: selectedCategory || undefined,
    vendorId: selectedVendor || undefined,
    search: searchQuery || undefined,
    active: true,
  });

  const products = productsData?.items || [];
  const totalProducts = productsData?.total || 0;

  const isLoading = categoriesLoading || vendorsLoading || productsLoading;

  // Format price in ILS
  const formatPrice = (price: number | null | undefined, currency = "ILS") => {
    if (price == null) return null;
    return new Intl.NumberFormat("he-IL", {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
    }).format(price);
  };

  // Parse dimensions from JSON string
  const parseDimensions = (dimensions: unknown) => {
    if (!dimensions) return null;
    if (typeof dimensions === "string") {
      try {
        return JSON.parse(dimensions);
      } catch {
        return null;
      }
    }
    return dimensions as { width: number; height: number; depth: number; unit: string };
  };

  return (
    <div className="min-h-dvh bg-gray-50">
      <Header />

      {/* Hero Banner */}
      <section className="bg-gradient-to-r from-primary-600 to-primary-800 pb-16 pt-28 text-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="mb-4 text-4xl font-bold">
            {t("catalog.title", "Product Catalog")}
          </h1>
          <p className="mx-auto max-w-2xl text-lg text-primary-100">
            {t(
              "catalog.subtitle",
              "Explore premium kitchen products from leading Israeli and international manufacturers"
            )}
          </p>
        </div>
      </section>

      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col gap-8 lg:flex-row">
          {/* Sidebar Filters */}
          <aside className="w-full shrink-0 lg:w-64">
            {/* Search */}
            <div className="mb-6">
              <label
                htmlFor="search"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                {t("catalog.search", "Search Products")}
              </label>
              <div className="relative">
                <input
                  id="search"
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t("catalog.searchPlaceholder", "Search by name...")}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 pl-10 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                />
                <svg
                  className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>

            {/* Categories */}
            <div className="mb-6">
              <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
                {t("catalog.categories", "Categories")}
              </h3>
              <div className="space-y-1">
                <button
                  onClick={() => setSelectedCategory(null)}
                  className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                    selectedCategory === null
                      ? "bg-primary-100 font-medium text-primary-700"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <span>{t("catalog.allCategories", "All Categories")}</span>
                  <span className="text-xs text-gray-500">{totalProducts}</span>
                </button>
                {categories?.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                      selectedCategory === category.id
                        ? "bg-primary-100 font-medium text-primary-700"
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    <span>{category.name}</span>
                    <span className="text-xs text-gray-500">
                      {category._count?.products || 0}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Vendors */}
            <div className="mb-6">
              <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
                {t("catalog.vendors", "Manufacturers")}
              </h3>
              <div className="space-y-1">
                <button
                  onClick={() => setSelectedVendor(null)}
                  className={`flex w-full items-center rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                    selectedVendor === null
                      ? "bg-primary-100 font-medium text-primary-700"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  {t("catalog.allVendors", "All Manufacturers")}
                </button>
                {vendors?.map((vendor) => (
                  <button
                    key={vendor.id}
                    onClick={() => setSelectedVendor(vendor.id)}
                    className={`flex w-full items-center rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                      selectedVendor === vendor.id
                        ? "bg-primary-100 font-medium text-primary-700"
                        : "text-gray-700 hover:bg-gray-100"
                    }`}
                  >
                    {vendor.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Clear Filters */}
            {(selectedCategory || selectedVendor || searchQuery) && (
              <button
                onClick={() => {
                  setSelectedCategory(null);
                  setSelectedVendor(null);
                  setSearchQuery("");
                }}
                className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100"
              >
                {t("catalog.clearFilters", "Clear Filters")}
              </button>
            )}
          </aside>

          {/* Product Grid */}
          <main className="flex-1">
            {/* Results Count */}
            <div className="mb-6 flex items-center justify-between">
              <p className="text-sm text-gray-600">
                {t("catalog.showing", "Showing {{count}} products", {
                  count: products.length,
                })}
              </p>
            </div>

            {/* Loading State */}
            {isLoading ? (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className="animate-pulse rounded-xl bg-white p-4 shadow-sm"
                  >
                    <div className="mb-4 aspect-square rounded-lg bg-gray-200" />
                    <div className="mb-2 h-4 w-3/4 rounded bg-gray-200" />
                    <div className="mb-4 h-3 w-1/2 rounded bg-gray-200" />
                    <div className="h-6 w-1/3 rounded bg-gray-200" />
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              /* Empty State */
              <div className="rounded-xl bg-white py-16 text-center shadow-sm">
                <svg
                  className="mx-auto mb-4 h-16 w-16 text-gray-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                  />
                </svg>
                <h3 className="mb-2 text-lg font-medium text-gray-900">
                  {t("catalog.noProducts", "No products found")}
                </h3>
                <p className="text-gray-500">
                  {t(
                    "catalog.tryDifferentFilters",
                    "Try adjusting your filters or search query"
                  )}
                </p>
              </div>
            ) : (
              /* Product Cards */
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {products.map((product) => {
                  const dimensions = parseDimensions(product.dimensions);
                  return (
                    <article
                      key={product.id}
                      className="group overflow-hidden rounded-xl bg-white shadow-sm transition-shadow hover:shadow-md"
                    >
                      {/* Product Image Placeholder */}
                      <div className="relative aspect-square bg-gradient-to-br from-gray-100 to-gray-200">
                        {/* Featured Badge */}
                        {product.featured && (
                          <span className="absolute left-3 top-3 rounded-full bg-amber-400 px-2 py-1 text-xs font-semibold text-amber-900">
                            {t("catalog.featured", "Featured")}
                          </span>
                        )}
                        {/* Vendor Badge */}
                        <span className="absolute right-3 top-3 rounded-full bg-white/90 px-2 py-1 text-xs font-medium text-gray-700 backdrop-blur-sm">
                          {product.vendor?.name}
                        </span>
                        {/* Placeholder Icon */}
                        <div className="flex h-full items-center justify-center">
                          <svg
                            className="h-20 w-20 text-gray-300"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1}
                              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                        </div>
                      </div>

                      {/* Product Info */}
                      <div className="p-4">
                        {/* Category Tag */}
                        <span className="mb-2 inline-block rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                          {product.category?.name}
                        </span>

                        <h3 className="mb-1 line-clamp-2 font-semibold text-gray-900 group-hover:text-primary-600">
                          {product.name}
                        </h3>

                        {/* Description */}
                        {product.description && (
                          <p className="mb-3 line-clamp-2 text-sm text-gray-500">
                            {product.description}
                          </p>
                        )}

                        {/* Dimensions */}
                        {dimensions && (
                          <p className="mb-3 text-xs text-gray-400">
                            {dimensions.width} × {dimensions.depth} × {dimensions.height}{" "}
                            {dimensions.unit}
                          </p>
                        )}

                        {/* Price & Action */}
                        <div className="flex items-center justify-between">
                          {product.price ? (
                            <span className="text-lg font-bold text-gray-900">
                              {formatPrice(product.price, product.currency)}
                            </span>
                          ) : (
                            <span className="text-sm text-gray-400">
                              {t("catalog.priceOnRequest", "Price on request")}
                            </span>
                          )}
                          <button className="rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-primary-700">
                            {t("catalog.addToDesign", "Add")}
                          </button>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 py-8 text-center text-gray-400">
        <p>&copy; 2025 BYK - Build Your Kitchen. All rights reserved.</p>
      </footer>
    </div>
  );
}
